import { useEffect, useState } from "react";
import { Music, Zap, Sparkles, Users, Heart, Instagram, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { cachedGet, fileUrl } from "@/lib/api";
import Reveal from "@/components/Reveal";

const LOVE = [
  { icon: Music, text: "Soulful Devotional Experience" },
  { icon: Zap, text: "Energetic & Engaging Performances" },
  { icon: Sparkles, text: "Blends Tradition with Modern Music" },
  { icon: Users, text: "Perfect for All Age Groups" },
  { icon: Heart, text: "Thousands of Devotees Connected" },
];

const QUOTES = [
  { text: "The energy, the music, the bhakti — Band Sumiran creates a divine atmosphere every single time!", name: "Priya Sharma", city: "Hyderabad" },
  { text: "It felt like a temple and a concert at once. Three thousand people singing the final aarti together — goosebumps for an hour.", name: "Meera K.", city: "Mumbai" },
  { text: "We booked Sumiran for our parents' 50th anniversary. The elders wept, the youngsters recorded everything.", name: "Rohit & Anjali S.", city: "Udaipur" },
];

export default function ThreeBand() {
  const [photos, setPhotos] = useState([]);
  const [qi, setQi] = useState(0);

  useEffect(() => {
    cachedGet("/gallery").then(({ data }) => setPhotos(data)).catch(() => {});
  }, []);

  const q = QUOTES[qi];

  return (
    <section className="px-6 md:px-12 py-14 md:py-16" data-testid="threeband-section">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 md:divide-x md:divide-bone/10">
        <Reveal>
          <div data-testid="why-love-panel">
            <h3 className="font-display text-sm tracking-[0.25em] text-gold">WHY PEOPLE LOVE US</h3>
            <ul className="mt-6 space-y-4">
              {LOVE.map((l, i) => (
                <li key={l.text} className="flex items-center gap-3" data-testid={`love-item-${i}`}>
                  <span className="w-8 h-8 rounded-full border border-saffron/50 flex items-center justify-center shrink-0">
                    <l.icon size={14} className="text-saffron" />
                  </span>
                  <span className="text-sm text-bone/80">{l.text}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 font-hindi text-5xl text-saffron/25 select-none" aria-hidden="true">ॐ</p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="md:px-8" data-testid="instagram-panel">
            <h3 className="font-display text-sm tracking-[0.25em] text-gold flex items-center gap-2">
              <Instagram size={15} /> FROM INSTAGRAM
            </h3>
            <div className="mt-6 grid grid-cols-4 md:grid-cols-2 lg:grid-cols-4 gap-2.5">
              {photos.slice(0, 4).map((p, i) => (
                <div key={p.id} className="relative rounded-md overflow-hidden aspect-square group" data-testid={`ig-thumb-${i}`}>
                  <img src={fileUrl(p.image_url)} alt={p.caption || "Instagram"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <Instagram size={13} className="absolute top-1.5 right-1.5 text-bone/90" />
                </div>
              ))}
            </div>
            <a href="https://www.instagram.com/thebandsumiran/" target="_blank" rel="noreferrer"
              className="mt-5 inline-flex items-center gap-2 px-6 py-2.5 border border-bone/25 text-bone text-[10px] font-bold tracking-[0.15em] rounded-full hover:border-saffron hover:text-saffron transition-colors"
              data-testid="ig-follow-btn">
              FOLLOW US @THEBANDSUMIRAN
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="md:pl-8" data-testid="devotees-panel">
            <h3 className="font-display text-sm tracking-[0.25em] text-gold">WHAT DEVOTEES SAY</h3>
            <div className="mt-6 relative">
              <span className="font-serif text-5xl text-saffron/40 leading-none select-none" aria-hidden="true">“</span>
              <p className="font-serif italic text-lg leading-relaxed text-bone/85 min-h-[110px]" data-testid="testimonial-text">{q.text}</p>
              <div className="mt-5 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-bone">{q.name}</p>
                  <p className="text-xs text-ash">{q.city}</p>
                  <p className="mt-1 flex gap-0.5">
                    {[...Array(5)].map((_, i) => <Star key={i} size={11} className="fill-gold text-gold" />)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setQi((qi - 1 + QUOTES.length) % QUOTES.length)}
                    className="w-9 h-9 rounded-full border border-bone/20 flex items-center justify-center text-bone/70 hover:border-saffron hover:text-saffron transition-colors"
                    data-testid="testimonial-prev" aria-label="Previous testimonial">
                    <ChevronLeft size={16} />
                  </button>
                  <button onClick={() => setQi((qi + 1) % QUOTES.length)}
                    className="w-9 h-9 rounded-full border border-bone/20 flex items-center justify-center text-bone/70 hover:border-saffron hover:text-saffron transition-colors"
                    data-testid="testimonial-next" aria-label="Next testimonial">
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
